/**
 * Auth helper
 */

import { _isNil, _isNotBlank, _isNotEmpty, _noop, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { StudyUserSummaryV1, StudyUserTransformer } from '@/model/User.model';
import { RouteService } from '@/support/RouteService';
import store from '@/store';

export const getCurrentUser = () => store.getState().user.currentUser;

/**
 * A toast to re-show on the login page once the post-logout reload completes.
 * Carried across the reload via history.state, since the in-memory app that
 * would normally raise the toast is destroyed by the reload.
 */
export interface LogoutMessage {
  level: 'info' | 'warning';
  title: string;
  body?: string;
}

/**
 * The reason a logout was triggered. The reason alone determines the post-logout
 * behaviour (toast to re-show, whether to return to the previous page, whether to
 * purge browser caches), so callers only express intent — never the mechanics.
 */
export type LogoutOptions =
  | { type: 'user' }
  | { type: 'inactivity' }
  | { type: 'session-expired'; returnTo?: string }
  | { type: 'outdated-client'; redirectTo: string };

type LogoutState = {
  message?: LogoutMessage;
  returnTo?: string;
};

interface LogoutBehaviour {
  /** Payload to stash on history.state for the login page; omitted when nothing to carry. */
  state?: LogoutState;
  clearBrowserStorage?: boolean;
  redirectTo?: string;
}

const behaviourFor = (options: LogoutOptions): LogoutBehaviour => {
  switch (options.type) {
    case 'inactivity':
      return { state: { message: { level: 'warning', title: 'Logged out due to inactivity' } } };
    case 'session-expired':
      return {
        state: {
          message: { level: 'info', title: 'Session expired', body: 'Please log in again' },
          returnTo: options.returnTo,
        },
      };
    case 'outdated-client':
      return { clearBrowserStorage: true, redirectTo: options.redirectTo };
    case 'user':
      return { clearBrowserStorage: true };
  }
};

const POST_LOGOUT_STATE_KEY = 'logout_state';

/**
 * Purge CacheStorage, cookies and IndexedDB in addition to web storage. Used by
 * the outdated-client recovery flow to guarantee no stale assets survive.
 */
const clearBrowserCaches = async (): Promise<void> => {
  // Best-effort: never let a rejection (or a synchronous throw such as Firefox not
  // implementing indexedDB.databases) escape and abort the logout redirect.
  try {
    if ('caches' in window) {
      await window.caches.keys().then((keys) => Promise.all(keys.map((key) => window.caches.delete(key).catch(_noop))));
    }
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
    if ('cookieStore' in window) {
      await window.cookieStore
        .getAll()
        // @ts-expect-error: not supported in all browsers
        .then((cookies) => Promise.all(cookies.map((cookie) => window.cookieStore.delete(cookie.name))));
    }
    if ('indexedDB' in window) {
      await indexedDB.databases().then((dbs) => {
        dbs.forEach((db) => {
          if (_notNil(db.name)) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      });
    }
  } catch {
    // swallow — clearing caches is best-effort and must not block logout
  }
};

/**
 * The current same-origin location as a relative path, for post-login restore.
 * Shared by the logout callers that carry a returnTo so they stay consistent.
 */
export const currentReturnTo = (): string => `${window.location.pathname}${window.location.search}`;

/**
 * Logs the current user out: clears all persisted local state and hard-navigates
 * to the login page. The full page reload discards the in-memory redux store, so
 * no stale session state can survive into the next login.
 *
 * The reason (see {@link LogoutOptions}) determines the post-logout payload — a
 * toast to re-show, and an optional return path for session-expiry logouts — which
 * is stashed on history.state via replaceState so it survives the reload, then read
 * back by the login page via {@link consumePostLogout}.
 */
export const logout = async (options: LogoutOptions = { type: 'user' }): Promise<void> => {
  const behaviour = behaviourFor(options);
  store.dispatch({ type: 'LOGOUT' });
  localStorage.clear();
  sessionStorage.clear();
  if (behaviour.clearBrowserStorage === true) {
    await clearBrowserCaches();
  }
  const target = behaviour.redirectTo ?? RouteService.web({ route: 'login' }).route;
  if (_isNil(behaviour.state)) {
    window.location.replace(target);
  } else {
    window.history.replaceState({ [POST_LOGOUT_STATE_KEY]: behaviour.state }, '', target);
    window.location.reload();
  }
};

/**
 * Read and clear the post-logout payload stashed on history.state before the reload.
 * The return path is validated as a same-origin relative URL to guard against
 * open redirects; anything else is discarded.
 */
export const consumePostLogout = (): LogoutState | undefined => {
  const state = window.history.state;
  const postLogout: LogoutState | undefined = state?.[POST_LOGOUT_STATE_KEY];
  if (_isNil(postLogout)) {
    return undefined;
  }
  const rest = { ...state };
  delete rest[POST_LOGOUT_STATE_KEY];
  window.history.replaceState(rest, '');
  const returnTo =
    _isNotBlank(postLogout.returnTo) && postLogout.returnTo.startsWith('/') && !postLogout.returnTo.startsWith('//')
      ? postLogout.returnTo
      : undefined;
  return { message: postLogout.message, returnTo };
};

type StudyUserCondition = (user: StudyUserTransformer) => boolean;

export function isStudyUser(study: Study | undefined, condition?: StudyUserCondition): boolean {
  const user = getCurrentUser();
  const users = (Array.isArray(study?.users) ? study?.users : study?.users?.data) ?? [];
  return users.some((u) => u.id === user.id && (condition?.(u) ?? true));
}

export function isAuthor(study: Study) {
  const user = getCurrentUser();
  return Number(user.id) === Number(study?.author?.id);
}

export const isOwner = (study: Study) => {
  const user = getCurrentUser();
  return Number(user.id) === Number(study?.owner?.id);
};

/**
 * Whether `study` is in an editable state: present, and not blocked by the
 * archived/cancelled guards. Shared by the write-user checks below.
 */
const isStudyEditable = (
  study: Study | undefined,
  disableArchived: boolean,
  disableCancelled: boolean
): study is Study => {
  if (_isNil(study)) {
    return false;
  }
  return !((disableArchived && study.archived_at) || (disableCancelled && study.canceled_at));
};

export function isWriteUserForStudy(study: Study, disableArchived = true, disableCancelled = true) {
  if (!isStudyEditable(study, disableArchived, disableCancelled)) {
    return false;
  }
  return isStudyUser(study, (u: StudyUserTransformer) => u.access === 'write') || isOwner(study) || isAuthor(study);
}

export const isWriteUserForStudyUsers = (studyUsers: Array<StudyUserSummaryV1>) => {
  if (_isNotEmpty(studyUsers)) {
    const user = getCurrentUser();
    return studyUsers.some((u) => u.id === user.id && u.access === 'write');
  }
  return false;
};

export const isWriteUserForStudyWithUsers = (
  study: Study | undefined,
  studyUsers: Array<StudyUserSummaryV1>,
  disableArchived = true,
  disableCancelled = true
): boolean => {
  if (!isStudyEditable(study, disableArchived, disableCancelled)) {
    return false;
  }
  return isWriteUserForStudyUsers(studyUsers) || isOwner(study) || isAuthor(study);
};

export function isCurrentTeamOwnerWithSettingsAccess() {
  try {
    const team = store.getState().team;
    return team.team.pivot.owner && !(team.features?.disable_admin_study_settings_access ?? false);
  } catch (e) {
    return false;
  }
}

export function isCurrentTeamOwner() {
  try {
    const team = store.getState().team.team;
    return team.pivot.owner;
  } catch (e) {
    return false;
  }
}

export const isCurrentTeamAdminConsoleUser = () => {
  try {
    return isCurrentTeamOwner() && store.getState().team.team.pivot.admin_console_user;
  } catch (e) {
    return false;
  }
};

export function hasStudiesReadAll(): boolean {
  try {
    return store.getState().team.team.studies_read_all ?? false;
  } catch (e) {
    return false;
  }
}
